import React, { useEffect, useMemo, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000StringArrayDisplay } from "../../../../../u2000Core/components/tables/display/U2000StringArrayDisplay";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { NS, U2104ROLES } from "../../../../U2104";
import U2104AffiliationActionDeleteDlg from "./U2104AffiliationActionDeleteDlg";
import U2104AffiliationActionEditDlg from "./U2104AffiliationActionEditDlg";
import { emptyLogicalUnit, type U2104AffiliationLogicalUnit, type U2104LogicalUnit } from "../../../../U2104Service";
import type { U2104AffiliationFormExtededHandlers } from "../../../U2104AffiliationForm";
import { u2104AffiliationActionHandler } from "./U2104AffiliationActionHandlers";
import { useParams } from "react-router-dom";

// Typage des props du composant
interface U2104AffiliationActionsProps {
  u2104AffiliationFormHandlers: U2104AffiliationFormExtededHandlers;
}

// Définition des colonnes pour le tableau des actions d'une unité logique
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "logicalUnitCode",
      label: t("user.lblLogicalUnitCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
    },
    {
      name: "logicalUnitDescription",
      label: t("user.lblLogicalUnitDescription", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[27%]",
      type: "string",
      align: "left",
    },
    {
      name: "actions",
      label: t("user.lblAction", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[60%]",
      type: "string",
      align: "left",
      display: (column, value, row) => {
        return <U2000StringArrayDisplay column={column} value={value} row={row} />;
      },
    },
  ];
}

// Composant principal pour la gestion des actions d'une unité logique
const U2104AffiliationActionsBody: React.FC<U2104AffiliationActionsProps> = ({ u2104AffiliationFormHandlers }) => {
  // Translation
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const { id: orgId } = useParams();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const gridStateHandlers = useU2000GridStateHandlers<U2104AffiliationLogicalUnit>();

  // Validation du formulaire pour les actions d'une unité logique
  const validateUserAction = (logicalUnit: U2104LogicalUnit) => {
    const result: Record<string, string> = {};
    if (logicalUnit.logicalUnitCode.trim() === "") {
      result["logicalUnitCode"] = "Logical Unit Code is required.";
    }
    return result;
  };

  // Business logic pour les actions d'une unité logique
  const u2103UserActionBL: U2000FormSBusinessHandlers<U2104LogicalUnit> = {
    newRecord: () => {
      return emptyLogicalUnit;
    },

    // validation du formulaire
    validateForm: validateUserAction,
  };

  // Handlers pour les actions d'une unité logique
  const u2104AffiliationActionformStateHandlers = useU2000FormStateHandlers<U2104LogicalUnit>({
    formState,
    formStateDispatch,
    businessHandlers: u2103UserActionBL,
  });

  // Handlers spécifiques pour les actions d'une unité logique (catalogue filtré org — BL-0270)
  const u2104AffiliationActionHandlers = u2104AffiliationActionHandler({
    u2000AxiosAuthenticated,
    orgId,
    gridStateHandlers,
    t,
    formStateHandlers: u2104AffiliationActionformStateHandlers,
  });

  // Définition des colonnes du tableau
  const columns = useMemo(() => getColumnsDef(t), [t]);

  // Transposer les données du formStateHandlers au gridStateHandlers pour LogicalUnitActions
  useEffect(() => {
    // Initialiser les colonnes et les données du tableau
    const currentColumns = gridStateHandlers.getColumns?.() ?? [];
    if (JSON.stringify(currentColumns) !== JSON.stringify(columns)) {
      gridStateHandlers.setSize(9999);
      gridStateHandlers.setColumns(columns);
      gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
      gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));
    }

    // Initialiser les données du tableau
    const logicalUnitActions = u2104AffiliationFormHandlers.getData?.()?.actions as U2104AffiliationLogicalUnit[];
    gridStateHandlers.setData(Array.isArray(logicalUnitActions) ? logicalUnitActions : []);
    gridStateHandlers.setTotalRows(Array.isArray(logicalUnitActions) ? logicalUnitActions.length : 0);

    // Gérer les erreurs spécifiques au champ LogicalUnitActions
    const errorsField = u2104AffiliationFormHandlers.getOtherErrors()?.roleActions;
    if (errorsField !== undefined) {
      gridStateHandlers.setErrors(Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []);
    } else {
      gridStateHandlers.setErrors([]);
    }
  }, [u2104AffiliationFormHandlers.getData(), t]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u2104AffiliationActionHandlers.dataChangedEvent === 0) return;

    const logicalUnitActions = gridStateHandlers.getData();
    u2104AffiliationFormHandlers.setData({
      ...u2104AffiliationFormHandlers.getData?.(),
      actions: logicalUnitActions,
    });
  }, [u2104AffiliationActionHandlers.dataChangedEvent]);

  // Désactiver le bouton de modification si aucun ou plus d'une ligne sélectionnée
  function disabledUpdateBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u2104AffiliationFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length !== 1
      ? true
      : false || "resetPwd" in (u2104AffiliationFormHandlers.getCustomAttributes?.() ?? {});
  }

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u2104AffiliationFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length === 0
      ? true
      : false || "resetPwd" in (u2104AffiliationFormHandlers.getCustomAttributes?.() ?? {});
  }

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u2104AffiliationActionHandlers.onAdd,
        hasRole: hasRole(U2104ROLES.ROLES),
        disabled: u2104AffiliationFormHandlers.getFormState?.() === U2000FormStateEnumState.READ || "resetPwd" in (u2104AffiliationFormHandlers.getCustomAttributes?.() ?? {}),
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u2104AffiliationActionHandlers.onModify,
        hasRole: hasRole(U2104ROLES.ROLES),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: u2104AffiliationActionHandlers.onDelete,
        hasRole: hasRole(U2104ROLES.ROLES),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u2104AffiliationActionHandlers]
  );

  // Render
  return (
    <>
      <U2104AffiliationActionEditDlg u2104AffiliationActionHandler={u2104AffiliationActionHandlers} t={t} NS={NS} />
      <U2104AffiliationActionDeleteDlg u2104AffiliationActionHandler={u2104AffiliationActionHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={u2104AffiliationFormHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        label={t("user.lblUserAction", {
          count: u2104AffiliationActionHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U2104AffiliationActionsBody;
